import {
  dermato_history_details,
  dermato_zone_evolution_duration,
  getDermatoSubReasonByValue,
} from "./dermato_models"
import { allergy_details, illness_details, PATH } from "./models"
import { ACTION, Answer, Question, Reply, Value } from "./types"
import { valueEquals } from "./utils"

/**
 * Question complémentaire affichée sous la question modifiée.
 * `answerId` est l'id sous lequel le reducer stocke sa réponse : la saga
 * recrée les questions de détail avec l'id de leur question parente.
 */
export interface PatchFollowup {
  question: Question
  answerId: string
}

// Questions injectées par la saga : elles ne sont pas déclarées en `subquestion`.
const SAGA_FOLLOWUPS: Record<string, PatchFollowup> = {
  [PATH.dermato_history]: {
    question: dermato_history_details,
    answerId: PATH.dermato_history,
  },
  [PATH.dermato_zone_evolution]: {
    question: dermato_zone_evolution_duration,
    answerId: PATH.dermato_zone_evolution_duration,
  },
}

const DETAILS_ANSWER_IDS: Record<string, string> = {
  [illness_details.id]: PATH.illness,
  [allergy_details.id]: PATH.allergy,
}

// Une ligne du résumé qui porte sur une sous-question renvoie vers sa question
// parente : c'est la réponse du parent qui décide de son affichage.
const PATCH_TARGETS: Record<string, string> = {
  [PATH.dermato_zone_evolution_duration]: PATH.dermato_zone_evolution,
}

export const getPatchTargetId = (questionId: string): string =>
  PATCH_TARGETS[questionId] ?? questionId

const followupForSubquestion = (subquestion: Question): PatchFollowup => ({
  question: subquestion,
  answerId: DETAILS_ANSWER_IDS[subquestion.id] ?? subquestion.id,
})

/**
 * Sous-question à afficher compte tenu des réponses sélectionnées.
 * Le motif est un cas à part : sa sous-question dépend du motif choisi.
 */
export const getPatchFollowup = (
  question: Question,
  selectedValues: Value[],
): PatchFollowup | undefined => {
  if (question.id === PATH.reason) {
    const subquestion = getDermatoSubReasonByValue(selectedValues[0] as string)
    return subquestion ? followupForSubquestion(subquestion) : undefined
  }

  const sagaFollowup = SAGA_FOLLOWUPS[question.id]
  if (sagaFollowup) {
    return selectedValues.includes(true) ? sagaFollowup : undefined
  }

  if (!question.subquestion) return undefined

  const opensSubquestion = question.replies.some(
    (reply: Reply) =>
      reply.action === ACTION.subquestion &&
      selectedValues.some((value) => valueEquals(value, reply.value)),
  )
  return opensSubquestion
    ? followupForSubquestion(question.subquestion)
    : undefined
}

/**
 * Réponses de sous-questions devenues obsolètes après modification : elles
 * doivent être effacées, sinon le résumé continue d'afficher l'ancienne valeur.
 */
export const getStaleFollowupAnswerIds = (
  question: Question,
  answers: Answer[],
  keptAnswerId?: string,
): string[] => {
  const candidates =
    question.id === PATH.reason
      ? answers
          .map(({ questionId }) => questionId)
          .filter((questionId) => questionId.startsWith(`${PATH.reason}:sub_`))
      : ([
          SAGA_FOLLOWUPS[question.id]?.answerId,
          question.subquestion &&
            followupForSubquestion(question.subquestion).answerId,
        ].filter(Boolean) as string[])

  return candidates.filter((answerId) => answerId !== keptAnswerId)
}

/** Réponses de la question, qu'elle soit autonome ou enfant d'un questions_line. */
export const getPatchAnswerValues = (
  answers: Answer[],
  questionId: string,
  lineParentId?: string,
): Value[] => {
  if (lineParentId) {
    const parentAnswer = answers.find(
      (answer) => answer.questionId === lineParentId,
    )
    return (parentAnswer?.value ?? [])
      .filter(
        (value) =>
          value !== null &&
          typeof value === "object" &&
          (value as { title?: string }).title === questionId,
      )
      .map((value) => (value as { value: Value }).value)
  }
  return answers.find((answer) => answer.questionId === questionId)?.value ?? []
}

/** Retrouve les `Reply` d'origine pour conserver leur `action` (sous-question). */
export const getRepliesForValues = (
  question: Question,
  values: Value[],
): Reply[] =>
  values.map(
    (value) =>
      question.replies.find((reply) => valueEquals(reply.value, value)) ??
      ({ title: String(value), value } as Reply),
  )
